import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  inject,
  OnDestroy,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { SvgIconComponent } from 'angular-svg-icon';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzToolTipModule } from 'ng-zorro-antd/tooltip';
import { DrawerService } from '../../../../../../core/services/drawer.service';
import { DRAWER_IDS } from '../../../../../../core/constants/drawer-ids.constant';
import {
  finalize,
  Subject,
  takeUntil,
  BehaviorSubject,
  filter,
  Observable,
  of,
} from 'rxjs';
import {
  BehaviorStrategyDetailPromptAnswerItem,
  Guideline,
  StrategyItem,
  StrategyQueryParams,
} from '../strategy.interface';
import { CommonModule } from '@angular/common';
import { ConfirmDialogComponent } from '../../../../../../components/confirm-dialog/confirm-dialog.component';
import { ManageBehaviorStrategiesService } from '../manage-behavior-strategies.service';
import { AuditContextService } from '../../../../../../features/audit-logs/audit-context.service';

import { ErrorResponse } from '../../../../../../core/models/error.models';
import { CommonService } from '../../../../../../shared/services/common.service';
import { CreateOwnStrategyComponent } from '../create-own-strategy/create-own-strategy.component';
import { FormsModule } from '@angular/forms';
import { NzModalModule } from 'ng-zorro-antd/modal';
import { ActivatedRoute, Router } from '@angular/router';
import { ManageStudentDrawerParams } from '../../../../insights.interface';
import { VideoModalComponent } from '../../../../../../components/video-modal/video-modal.component';
import { PdfService } from '../../../../../../core/services/pdf.service';
import { LoaderService } from '../../../../../../core/services/loader.service';
import { DrawerHeaderComponent } from '../../../../../../components/drawer-header/drawer-header.component';
import { NzSwitchModule } from 'ng-zorro-antd/switch';
import { OrganizationModel } from '../../../../../organizations/organization.interface';
import { OrganizationService } from '../../../../../organizations/organization.service';
import { AuthService } from '../../../../../../core/services/auth.service';
import { CmsRoleAccessService } from '../../../../../../core/services/cms-role-access.service';
import { UserRole } from '../../../../../../features/activity-dashboard/activity-dashboard.enums';

interface ProactiveStrategyDrawerParams {
  data: StrategyItem;
  mode: 'view' | 'edit' | 'create' | 'guide';
  behaviorId?: number;
  caseParams: ManageStudentDrawerParams;
}

@Component({
  selector: 'app-proactive-strategy',
  standalone: true,
  imports: [
    SvgIconComponent,
    NzButtonModule,
    NzToolTipModule,
    CommonModule,
    ConfirmDialogComponent,
    FormsModule,
    NzModalModule,
    VideoModalComponent,
    DrawerHeaderComponent,
    NzSwitchModule,
    FormsModule,
  ],
  templateUrl: './proactive-strategy.component.html',
  styleUrl: './proactive-strategy.component.scss',
})
export class ProactiveStrategyComponent implements OnDestroy {
  @ViewChild('pdfContent') pdfContent!: ElementRef;
  @ViewChild('drawerBehaviorEdit')
  drawerBehaviorEdit!: TemplateRef<void>;

  private readonly router = inject(Router);
  private readonly strategyService = inject(ManageBehaviorStrategiesService);
  private readonly drawerService = inject(DrawerService);
  private readonly commonService = inject(CommonService);
  private readonly auditContextService = inject(AuditContextService);
  private readonly authService = inject(AuthService);
  private readonly cmsRoleAccessService = inject(CmsRoleAccessService);
  private readonly destroy$ = new Subject<void>();
  private readonly paramsSubject =
    new BehaviorSubject<StrategyQueryParams | null>(null);

  private originalStrategyActiveState = true;
  userRoles: string[] = [];
  roleEnum = UserRole;
  allowManageStrategy = true;
  allowDeleteStrategy = true;

  readonly defaultParams$ = this.paramsSubject.asObservable();
  readonly currentYear: number = new Date().getFullYear();
  readonly confrimDialogMsg = 'Are you sure you want to delete this strategy?';

  strategy?: StrategyItem;
  mode: 'view' | 'edit' | 'create' | 'guide' = 'view';
  showConfirmDialog = false;
  isDeleting = false;
  behaviorId?: number;

  isVisible = false;
  statusChangeReason = '';
  isStrategyActive = true;
  isStrategyId = false;
  strategyId = '';

  isApprovalModalVisible = false;
  isApproving = false;
  approvalComments = '';
  isExpired = false;
  iconColor = '';
  caseParams!: ManageStudentDrawerParams;
  orgDetail: OrganizationModel | null = null;
  logoURl = '';
  printLogoUrl = '';

  isVideoVisible = false;
  loading$: Observable<boolean> = of(false);

  private lastDetailPromptAnswersRef?: BehaviorStrategyDetailPromptAnswerItem[];
  private cachedSortedDetailPromptAnswers: BehaviorStrategyDetailPromptAnswerItem[] =
    [];

  get hasDetailPromptAnswers(): boolean {
    return !!this.strategy?.detailPromptAnswers?.length;
  }

  get visibleGuidelines(): Guideline[] {
    return (this.strategy?.guidelines || []).filter(item =>
      item.guideline?.trim()
    );
  }

  get hasGuidelines(): boolean {
    return !!this.visibleGuidelines.length;
  }

  get sortedDetailPromptAnswers(): BehaviorStrategyDetailPromptAnswerItem[] {
    const detailPromptAnswers = this.strategy?.detailPromptAnswers;

    if (!detailPromptAnswers?.length) {
      this.lastDetailPromptAnswersRef = detailPromptAnswers;
      this.cachedSortedDetailPromptAnswers = [];
      return this.cachedSortedDetailPromptAnswers;
    }

    if (this.lastDetailPromptAnswersRef === detailPromptAnswers) {
      return this.cachedSortedDetailPromptAnswers;
    }

    this.lastDetailPromptAnswersRef = detailPromptAnswers;
    this.cachedSortedDetailPromptAnswers = [...detailPromptAnswers].sort(
      (a, b) => a.sequenceNumber - b.sequenceNumber
    );

    return this.cachedSortedDetailPromptAnswers;
  }

  constructor(
    private readonly cdr: ChangeDetectorRef,
    private readonly route: ActivatedRoute,
    private readonly pdfService: PdfService,
    private readonly loaderService: LoaderService,
    private readonly orgService: OrganizationService
  ) {
    this.loading$ = this.loaderService.loading$;
    this.authService.userRoles$
      .pipe(takeUntil(this.destroy$))
      .subscribe(roles => {
        if (roles) {
          this.userRoles = roles;
          this.allowManageStrategy =
            this.cmsRoleAccessService.computeAllowFromCmsRole(this.userRoles, [
              this.roleEnum.CMSManager,
              this.roleEnum.CMSDataEntry,
              this.roleEnum.CMSBrowser,
            ]);
          this.allowDeleteStrategy =
            this.cmsRoleAccessService.computeAllowFromCmsRole(this.userRoles, [
              this.roleEnum.CMSDataEntry,
              this.roleEnum.CMSBrowser,
            ]);
        }
      });
    this.setupDrawerSubscription();
    this.route.queryParams.pipe(takeUntil(this.destroy$)).subscribe(params => {
      this.isStrategyId = params['strategy'];
      if (params['strategy']) {
        this.isStrategyId = true;
        this.strategyId = params['strategy'];
      }
    });
  }

  private setupDrawerSubscription(): void {
    this.drawerService
      .getParams<ProactiveStrategyDrawerParams>(
        DRAWER_IDS.MANAGE_BEHAVIOR_STRATEGIES_DETAIL
      )
      .pipe(
        takeUntil(this.destroy$),
        filter(params => !!params?.data)
      )
      .subscribe(params => {
        if (params) {
          this.caseParams = params.caseParams;
          this.strategy = params.data;
          this.strategyId = String(this.strategy.id);
          const organizationId = this.caseParams?.case?.student?.organizationId;
          if (organizationId) {
            this.getOrgDetails(organizationId);
          }
          if (
            this.strategy?.expirationTime &&
            new Date(this.strategy.expirationTime).getTime() < Date.now()
          ) {
            this.isExpired = true;
            this.commonService.notify('info', 'Strategy link is expired');
          }
          this.behaviorId = params.behaviorId;
          this.isStrategyActive = this.strategy.statusId === 1;
          this.originalStrategyActiveState = this.isStrategyActive;
        }
        this.mode = params?.mode ?? 'view';
      });
  }

  getOrgDetails(orgId: number) {
    this.loaderService.show();
    this.orgService
      .getOrganization(orgId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: response => {
          if (response) {
            this.orgDetail = response;
            if (this.orgDetail?.logoUrl) {
              this.logoURl = this.orgDetail.logoUrl;
            }
          }
          this.loaderService.hide();
        },
        error: error => {
          this.loaderService.hide();
          this.commonService.notify('error', error.message);
        },
      });
  }

  onDelete(): void {
    if (!this.allowDeleteStrategy) {
      return;
    }
    this.showConfirmDialog = true;
  }

  handleDelete(): void {
    if (!this.strategy?.caseBehaviorStrategyId) return;

    this.isDeleting = true;
    this.deleteStrategy();
  }

  handleCancel(): void {
    this.showConfirmDialog = false;
    this.isApprovalModalVisible = false;
    this.approvalComments = '';

    if (this.isVisible) {
      this.isVisible = false;
      this.statusChangeReason = '';
      this.isStrategyActive = this.originalStrategyActiveState;
    }

    this.cdr.detectChanges();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.paramsSubject.complete();
  }

  toggleStatus(): void {
    if (this.isStrategyActive) {
      this.isVisible = true;
    } else {
      this.updateStatus();
    }
  }

  updateStatus(): void {
    this.loaderService.show();

    const payload = {
      caseBehaviorStrategyId: this.strategy?.caseBehaviorStrategyId ?? 0,
      statusId: this.isStrategyActive ? 2 : 1,
      statusChageReason: this.isStrategyActive ? this.statusChangeReason : '',
    };

    const refreshStrategyList = () => {
      return this.strategyService
        .getBehaviorStrategyList({
          caseBehaviorId: this.behaviorId ?? 0,
          strategyType: this.strategy?.behaviourStrategyTypeId ?? undefined,
          pageIndex: 1,
          pageSize: 10,
        })
        .pipe(takeUntil(this.destroy$));
    };

    this.strategyService
      .updateStrategyStatus(payload)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.isVisible = false;
          this.statusChangeReason = '';
          this.isStrategyActive = !this.isStrategyActive;
          this.cdr.detectChanges();

          this.commonService.notify(
            'success',
            `Strategy ${this.isStrategyActive ? 'activated' : 'deactivated'} successfully`
          );
        },
        error: ({ errors, message }: ErrorResponse) => {
          const errorMessages = errors.map(
            err => err.description || err.detailedMessage
          );
          this.commonService.notify('error', errorMessages, message);
          this.loaderService.hide();
        },
        complete: () => {
          refreshStrategyList()
            .pipe(takeUntil(this.destroy$))
            .subscribe({
              next: () => {
                this.closeDrawers();
                this.loaderService.hide();
              },
              error: () => {
                this.loaderService.hide();
              },
            });
        },
      });
  }

  closeDrawers(): void {
    this.drawerService.closeDrawer(DRAWER_IDS.CREATE_OWN_STRATEGY);
  }

  closeManageBehaviorDetails(): void {
    this.drawerService.closeDrawer(DRAWER_IDS.CREATE_OWN_STRATEGY);
  }

  handleDeactivated(): void {
    this.isVisible = false;
    this.statusChangeReason = '';
    this.isStrategyActive = this.originalStrategyActiveState;
    this.cdr.detectChanges();
  }

  openCreateOwnStrategyComponent(): void {
    if (!this.allowManageStrategy) {
      return;
    }
    this.drawerService
      .openDrawer(
        CreateOwnStrategyComponent,
        this.drawerBehaviorEdit as unknown as TemplateRef<object>,
        1170,
        true,
        undefined,
        {
          strategy: this.strategy,
          behaviorId: this.behaviorId,
          mode: 'edit',
          caseParams: this.caseParams,
        },
        DRAWER_IDS.CREATE_OWN_STRATEGY
      )
      .afterClose.pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.strategyService
          .getBehaviorStrategyDetailsById(
            this.strategy?.id ?? 0,
            this.behaviorId
          )
          .pipe(takeUntil(this.destroy$))
          .subscribe(updatedStrategy => {
            if (updatedStrategy) {
              this.strategy = updatedStrategy;
              this.cdr.detectChanges();
            }
          });
      });
  }

  openVideoModal() {
    this.isVideoVisible = true;
  }

  private readonly detailPromptLookup = new Map<number, string>();
  private detailPromptLookupSource:
    | StrategyItem['behaviorStrategyDetailPrompts']
    | undefined;

  private ensureDetailPromptLookup(): void {
    const prompts = this.strategy?.behaviorStrategyDetailPrompts;

    if (this.detailPromptLookupSource === prompts) {
      return;
    }

    this.detailPromptLookupSource = prompts;
    this.detailPromptLookup.clear();

    prompts?.forEach(prompt => {
      this.detailPromptLookup.set(prompt.id, prompt.detailPrompt || '');
    });
  }

  getDetailPromptAnswerQuestion(
    detailPromptAnswer: BehaviorStrategyDetailPromptAnswerItem
  ): string {
    this.ensureDetailPromptLookup();

    return (
      this.detailPromptLookup.get(
        detailPromptAnswer.behaviorStrategyDetailPromptId
      ) ||
      detailPromptAnswer.detailPrompt ||
      ''
    );
  }

  private deleteStrategy(): void {
    this.strategyService
      .deleteStrategy(this.strategy?.caseBehaviorStrategyId ?? 0)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.isDeleting = false;
          this.showConfirmDialog = false;
          this.showConfirmDialog = false;
          this.cdr.detectChanges();
        })
      )
      .subscribe({
        next: () => this.handleDeleteSuccess(),
        error: (error: ErrorResponse) => this.handleDeleteError(error),
      });
  }

  private handleDeleteSuccess(): void {
    this.commonService.notify('success', 'Strategy deleted successfully');
    this.strategyService
      .getBehaviorStrategyList({
        caseBehaviorId: this.behaviorId,
        strategyType: this.strategy?.behaviourStrategyTypeId ?? 0,
        pageIndex: 1,
        pageSize: 10,
      })
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.cdr.detectChanges();
          this.isDeleting = false;
        })
      )
      .subscribe({
        next: () => {
          this.closeDrawers();
          this.drawerService.closeDrawer(
            DRAWER_IDS.MANAGE_BEHAVIOR_STRATEGIES_DETAIL
          );
        },
        error: ({ errors, message }: ErrorResponse) => {
          const errorMessages = errors.map(
            err => err.description || err.detailedMessage
          );
          this.commonService.notify('error', errorMessages, message);
        },
      });
  }

  private handleDeleteError({ errors, message }: ErrorResponse): void {
    const errorMessages = errors.map(
      err => err.description || err.detailedMessage
    );
    this.commonService.notify('error', errorMessages, message);
  }

  showApprovalModal(strategyId: string, isApprove: boolean): void {
    this.isApprovalModalVisible = true;
    this.isApproving = isApprove;
    this.strategyId = strategyId;
    this.iconColor = isApprove ? 'green' : 'red';
  }

  submitApproval(): void {
    // Build audit log for approval action
    const oldValue: { isApproved: null | boolean; comments: string } = {
      isApproved: null,
      comments: '',
    };
    const newValue: { isApproved: boolean; comments: string } = {
      isApproved: this.isApproving,
      comments: this.approvalComments,
    };
    const modifiedFields = this.auditContextService.buildFieldChanges(
      oldValue,
      newValue,
      {
        sensitiveFields: [],
        labelMap: {
          isApproved: 'Approval Status',
          comments: 'Approval Comments',
        },
      }
    );
    const payload = {
      isApproved: this.isApproving,
      comments: this.approvalComments,
      _modifiedFields: modifiedFields,
    };
    this.loaderService.show();

    this.strategyService.approveStrategy(this.strategyId, payload).subscribe({
      next: () => {
        this.isApprovalModalVisible = false;
        this.approvalComments = '';
        if (this.strategy) {
          this.strategy.isPublish = this.isApproving;
          this.strategy.isRejected = !this.isApproving;
        }
        this.commonService.notify(
          'success',
          `Strategy ${this.isApproving ? 'approved' : 'rejected'} successfully`
        );

        this.cdr.detectChanges();
        this.loaderService.hide();
      },
      error: error => {
        this.commonService.notify(
          'error',
          'Error updating strategy approval status'
        );
        console.error('Error updating approval status:', error);
      },
      complete: () => {
        this.isApprovalModalVisible = false;
        this.approvalComments = '';
        this.loaderService.hide();
        this.cdr.detectChanges();
      },
    });
  }

  async getStrategyPdf(): Promise<void> {
    try {
      const logoUrl = this.logoURl || '/assets/images/logo.png';
      const base64Image =
        await this.commonService.getBase64ImageFromUrl(logoUrl);
      this.printLogoUrl = base64Image;
      this.cdr.detectChanges();

      await this.pdfService.generatePDF({
        element: this.pdfContent.nativeElement,
        fileName: `${this.strategy?.title}_Strategy.pdf`,
        excludeElements: ['.no-print', '.no-active'],
        customConfig: {
          pageSize: 'A4',
          pageOrientation: 'portrait',
        },
        printData: {
          logo: base64Image,
          orgDetail: {
            address: this.orgDetail?.address || '',
            city: this.orgDetail?.city || '',
            state: this.orgDetail?.state || '',
            country: this.orgDetail?.country || '',
            zipCode: this.orgDetail?.zipCode || '',
          },
          caseDetail: {
            student: {
              firstName: this.caseParams?.case?.student?.firstName || '',
              lastName: this.caseParams?.case?.student?.lastName || '',
            },
            supervisorName: this.caseParams?.case?.supervisorName || '-',
          },
        },
      });
    } catch {
      this.commonService.notify('error', 'Failed to generate PDF');
      this.cdr.detectChanges();
    }
  }
}
